import React, { useContext } from 'react';

import { ProgressBar } from 'components/ProgressBar';
import Modal, { ModalContents, ModalContentsProps, ModalTypography } from 'components/Modal/Modal';

import { NonprofitOnboardingFlowContext } from '../flows/nonprofitOnboarding/context';
import { NonprofitOnboardingDataCaptureFlowContext } from '../flows/nonprofitOnboardingDataCapture/context';

/**
 * A component for a flow step that uses a modal to display.
 */
export const ModalStep = ({
  children,
  actions,
  header,
  headerStyles,
  showProgressBar = false,
  showHelpCenterLink = true,
}: {
  children: React.ReactNode;
  showProgressBar?: boolean;
  showHelpCenterLink?: boolean;
  currentStep?: number;
} & Omit<ModalContentsProps, 'onClickClose' | 'body'>): JSX.Element => {
  const { isTransitionAllowed, send, state } = useContext(NonprofitOnboardingFlowContext) || {};
  const { state: dataCaptureState } = useContext(NonprofitOnboardingDataCaptureFlowContext) || {};

  const allowedSteps = state?.context?.allowedSteps || [];

  const getStateIndex = (stepName: string) =>
    allowedSteps.indexOf(stepName) === -1 ? 0 : allowedSteps.indexOf(stepName);
  const stepCount = allowedSteps.length;
  const stepName = typeof state?.value === 'object' ? Object.keys(state?.value)[0] : state?.value;
  const dataCaptureStepName =
    typeof dataCaptureState?.value === 'object'
      ? Object.keys(dataCaptureState?.value)[0]
      : dataCaptureState?.value;
  // @NOTE-DP: we need to get current step either from top level machine or from the data capture machine (gosh I wish we didn't break these machines apart)
  const currentStep = getStateIndex(stepName) + getStateIndex(dataCaptureStepName) + 1;

  const onClose = isTransitionAllowed?.('DISMISS') ? () => send?.('DISMISS') : undefined;

  return (
    <Modal
      disableEnforceFocus
      style={{
        // @NOTE-DP: we want the top menu to be above it
        zIndex: 1200,
      }}
      open
      fullWidth
      maxWidth="md"
      onClose={onClose}
    >
      <ModalContents
        onClickClose={onClose}
        header={header}
        headerStyles={headerStyles}
        body={
          <>
            {children}
            {showHelpCenterLink && (
              <ModalTypography style={{ paddingTop: 16 }}>
                <a
                  href="https://support.joindeed.com/hc/en-us"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Please visit our help center for further reference.
                </a>
              </ModalTypography>
            )}
          </>
        }
        actions={
          showProgressBar && stepCount > 0 ? (
            <ProgressBar
              {...{
                stepCount,
                currentStep,
                actions,
              }}
            />
          ) : (
            actions
          )
        }
      />
    </Modal>
  );
};
